import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import usePageMetadata from '../../hooks/usePageMetadata';
import {
  METRIC_THREAD_DESCRIPTION,
  METRIC_THREAD_H1,
  METRIC_THREAD_OPEN_GRAPH,
  METRIC_THREAD_STRUCTURED_DATA,
  METRIC_THREAD_TITLE,
  METRIC_THREAD_TWITTER,
  METRIC_THREAD_URL,
} from '../../shared/metricThreadSeo.mjs';
import {
  calculateMetricPreDrill,
  calculateMetricThread,
  getMetricThreadCatalog,
} from '../../services/threadService';
import MetricThreadResult from './MetricThreadResult';
import MetricThreadSeoContent from './MetricThreadSeoContent';
import { formatMetricIdentityNumber, metricPitchLabels } from './metricThreadDisplay';
import './MetricThreadPage.css';

const PITCH_CATEGORIES = ['default_coarse', 'fine', 'other'];
const preferredToleranceClass = side => side === 'internal' ? '6H' : '6g';
const supportedToleranceDefault = (side, values = []) => (
  values.includes(preferredToleranceClass(side)) ? preferredToleranceClass(side) : values[0] || ''
);
const positiveNumber = value => typeof value === 'string'
  && /^(?:\d+\.?\d*|\.\d+)$/.test(value.trim())
  && Number(value) > 0;
const completePositiveDecimal = value => typeof value === 'string'
  && /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(value.trim())
  && Number(value) > 0;

const parseQuery = (searchParams) => {
  const requestedSide = searchParams.get('side');
  const nominal = searchParams.get('nominal') || '';
  const pitch = searchParams.get('pitch') || '';
  const tolerance = searchParams.get('tolerance') || '';
  return {
    side: requestedSide === 'external' ? 'external' : 'internal',
    nominal: positiveNumber(nominal) ? formatMetricIdentityNumber(nominal) : '',
    pitch: positiveNumber(pitch) ? formatMetricIdentityNumber(pitch) : '',
    tolerance,
    complete: (requestedSide === 'internal' || requestedSide === 'external')
      && positiveNumber(nominal) && positiveNumber(pitch) && tolerance.length > 0,
  };
};

const MetricThreadPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const querySignature = searchParams.toString();
  const initialQuery = useMemo(() => parseQuery(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [side, setSide] = useState(initialQuery.side);
  const [nominalMode, setNominalMode] = useState(initialQuery.nominal ? 'custom' : 'listed');
  const [nominal, setNominal] = useState(initialQuery.nominal);
  const [pitchMode, setPitchMode] = useState(initialQuery.pitch ? 'custom' : 'listed');
  const [pitch, setPitch] = useState(initialQuery.pitch);
  const [toleranceClass, setToleranceClass] = useState(initialQuery.tolerance);
  const [summary, setSummary] = useState(null);
  const [nominalCatalog, setNominalCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [summaryCatalogError, setSummaryCatalogError] = useState('');
  const [nominalCatalogError, setNominalCatalogError] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [preDrillResult, setPreDrillResult] = useState(null);
  const [preDrillLoading, setPreDrillLoading] = useState(false);
  const [preDrillError, setPreDrillError] = useState('');
  const [formingDiameterInput, setFormingDiameterInput] = useState('');
  const summaryRequestRef = useRef(null);
  const nominalRequestRef = useRef(null);
  const calculationRequestRef = useRef(null);
  const preDrillRequestRef = useRef(null);
  const processedQueryRef = useRef('');
  const queryHydrationRef = useRef(null);

  usePageMetadata(
    METRIC_THREAD_TITLE,
    METRIC_THREAD_DESCRIPTION,
    {
      canonical: METRIC_THREAD_URL,
      robots: querySignature ? 'noindex, follow' : 'index, follow',
      openGraph: METRIC_THREAD_OPEN_GRAPH,
      twitter: METRIC_THREAD_TWITTER,
      structuredData: METRIC_THREAD_STRUCTURED_DATA,
      structuredDataId: 'metric-thread-structured-data',
    }
  );

  useLayoutEffect(() => {
    document.getElementById('metric-thread-initial-content')?.remove();
  }, []);

  const clearCalculation = useCallback(() => {
    const controller = calculationRequestRef.current;
    calculationRequestRef.current = null;
    controller?.abort();
    const preDrillController = preDrillRequestRef.current;
    preDrillRequestRef.current = null;
    preDrillController?.abort();
    setLoading(false);
    setPreDrillLoading(false);
    setResult(null);
    setPreDrillResult(null);
    setPreDrillError('');
    setFormingDiameterInput('');
    setError('');
  }, []);

  useEffect(() => () => {
    [summaryRequestRef, nominalRequestRef, calculationRequestRef, preDrillRequestRef].forEach((requestRef) => {
      const controller = requestRef.current;
      requestRef.current = null;
      controller?.abort();
    });
  }, []);

  useEffect(() => {
    const query = parseQuery(searchParams);
    processedQueryRef.current = '';
    queryHydrationRef.current = {
      signature: querySignature,
      side: query.side,
      nominal: query.nominal,
      pitch: query.pitch,
    };
    setSide(query.side);
    setNominal(query.nominal);
    setNominalMode(query.nominal ? 'custom' : 'listed');
    setPitch(query.pitch);
    setPitchMode(query.pitch ? 'custom' : 'listed');
    setToleranceClass(query.tolerance);
    clearCalculation();
  }, [querySignature]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const previousController = summaryRequestRef.current;
    summaryRequestRef.current = null;
    previousController?.abort();
    const controller = new AbortController();
    summaryRequestRef.current = controller;
    setCatalogLoading(true);
    setSummaryCatalogError('');
    getMetricThreadCatalog({ side }, { signal: controller.signal })
      .then(data => {
        if (summaryRequestRef.current !== controller) return;
        setSummary(data);
        setSummaryCatalogError('');
        const supportedClasses = data.toleranceClasses?.values || [];
        setToleranceClass(current => supportedClasses.includes(current)
          ? current
          : supportedToleranceDefault(side, supportedClasses));
        if (toleranceClass && !supportedClasses.includes(toleranceClass)) clearCalculation();
      })
      .catch(requestError => {
        if (requestError?.code === 'ERR_CANCELED' || summaryRequestRef.current !== controller) return;
        setSummary(null);
        setSummaryCatalogError(requestError?.response?.data?.message || 'Metric thread choices could not be loaded. Please try again.');
      })
      .finally(() => {
        if (summaryRequestRef.current === controller) {
          summaryRequestRef.current = null;
          setCatalogLoading(false);
        }
      });
    return () => {
      if (summaryRequestRef.current === controller) summaryRequestRef.current = null;
      controller.abort();
    };
  }, [side]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const previousController = nominalRequestRef.current;
    nominalRequestRef.current = null;
    previousController?.abort();
    setNominalCatalog(null);
    setNominalCatalogError('');
    if (!positiveNumber(nominal)) return undefined;
    const controller = new AbortController();
    nominalRequestRef.current = controller;
    getMetricThreadCatalog({ nominalMm: Number(nominal), side }, { signal: controller.signal })
      .then(data => {
        if (nominalRequestRef.current !== controller) return;
        setNominalCatalog(data);
        setNominalCatalogError('');
      })
      .catch(requestError => {
        if (requestError?.code === 'ERR_CANCELED' || nominalRequestRef.current !== controller) return;
        setNominalCatalogError(requestError?.response?.data?.message || 'Pitches could not be loaded for this diameter.');
      })
      .finally(() => {
        if (nominalRequestRef.current === controller) nominalRequestRef.current = null;
      });
    return () => {
      if (nominalRequestRef.current === controller) nominalRequestRef.current = null;
      controller.abort();
    };
  }, [nominal, side]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const hydration = queryHydrationRef.current;
    if (!hydration || !hydration.nominal || !nominalCatalog
      || hydration.signature !== querySignature || hydration.side !== side
      || Number(hydration.nominal) !== Number(nominal)
      || Number(nominalCatalog?.nominalMm) !== Number(nominal)) return;
    setNominalMode(nominalCatalog.listed ? 'listed' : 'custom');
    const listedPitch = hydration.pitch
      && nominalCatalog.pitches.some(item => Number(item.pitchMm) === Number(hydration.pitch));
    setPitchMode(listedPitch ? 'listed' : 'custom');
    queryHydrationRef.current = null;
  }, [nominal, nominalCatalog, querySignature, side]);

  const runCalculation = useCallback(async (input) => {
    calculationRequestRef.current?.abort();
    preDrillRequestRef.current?.abort();
    const controller = new AbortController();
    calculationRequestRef.current = controller;
    preDrillRequestRef.current = null;
    setLoading(true);
    setResult(null);
    setError('');
    setPreDrillResult(null);
    setPreDrillError('');
    setPreDrillLoading(false);
    try {
      const nextResult = await calculateMetricThread(input, { signal: controller.signal });
      if (calculationRequestRef.current !== controller) return;
      setResult(nextResult);
      if (input.side === 'internal') {
        const preDrillController = new AbortController();
        preDrillRequestRef.current = preDrillController;
        setPreDrillLoading(true);
        calculateMetricPreDrill({
          nominalMm: input.nominalMm,
          pitchMm: input.pitchMm,
          toleranceClass: input.toleranceClass,
        }, { signal: preDrillController.signal })
          .then(data => {
            if (preDrillRequestRef.current === preDrillController) {
              setPreDrillResult(data);
              const recommendedDiameter = data?.forming?.recommendedPreDrillDiameterMm;
              setFormingDiameterInput(Number.isFinite(Number(recommendedDiameter)) ? String(recommendedDiameter) : '');
            }
          })
          .catch(requestError => {
            if (requestError?.code === 'ERR_CANCELED' || preDrillRequestRef.current !== preDrillController) return;
            setPreDrillError(requestError?.response?.data?.message || 'The pre-drill recommendation could not be loaded.');
          })
          .finally(() => {
            if (preDrillRequestRef.current === preDrillController) {
              preDrillRequestRef.current = null;
              setPreDrillLoading(false);
            }
          });
      }
    } catch (requestError) {
      if (requestError?.code === 'ERR_CANCELED' || calculationRequestRef.current !== controller) return;
      setError(requestError?.response?.data?.message || 'The Metric thread calculation could not be completed. Please try again.');
    } finally {
      if (calculationRequestRef.current === controller) {
        calculationRequestRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!result || result.input.side !== 'internal' || !preDrillResult
      || !completePositiveDecimal(formingDiameterInput)) return undefined;
    const selectedDiameter = Number(formingDiameterInput);
    if (selectedDiameter === Number(preDrillResult.forming?.selectedPreDrillDiameterMm)) return undefined;

    let controller = null;
    const timeout = setTimeout(() => {
      preDrillRequestRef.current?.abort();
      controller = new AbortController();
      preDrillRequestRef.current = controller;
      setPreDrillLoading(true);
      setPreDrillError('');
      calculateMetricPreDrill({
        nominalMm: result.input.nominalMm,
        pitchMm: result.input.pitchMm,
        toleranceClass: result.input.toleranceClass,
        preDrillDiameterMm: selectedDiameter,
      }, { signal: controller.signal })
        .then(data => {
          if (preDrillRequestRef.current === controller) setPreDrillResult(data);
        })
        .catch(requestError => {
          if (requestError?.code === 'ERR_CANCELED' || preDrillRequestRef.current !== controller) return;
          setPreDrillError(requestError?.response?.data?.message || 'The selected forming pre-drill diameter could not be calculated.');
        })
        .finally(() => {
          if (preDrillRequestRef.current === controller) {
            preDrillRequestRef.current = null;
            setPreDrillLoading(false);
          }
        });
    }, 350);

    return () => {
      clearTimeout(timeout);
      if (controller && preDrillRequestRef.current === controller) {
        preDrillRequestRef.current = null;
        controller.abort();
      }
    };
  }, [formingDiameterInput, preDrillResult, result]);

  useEffect(() => {
    const query = parseQuery(searchParams);
    if (!query.complete || !summary || processedQueryRef.current === querySignature) return;
    if (side !== query.side || Number(nominal) !== Number(query.nominal)
      || Number(pitch) !== Number(query.pitch) || toleranceClass !== query.tolerance) return;
    if (!summary.toleranceClasses?.values.includes(query.tolerance)) return;
    processedQueryRef.current = querySignature;
    runCalculation({
      nominalMm: Number(query.nominal),
      pitchMm: Number(query.pitch),
      side: query.side,
      toleranceClass: query.tolerance,
    });
  }, [nominal, pitch, querySignature, searchParams, side, summary, toleranceClass, runCalculation]);

  const updateSide = (event) => {
    queryHydrationRef.current = null;
    setSide(event.target.value);
    setToleranceClass('');
    clearCalculation();
  };

  const updateNominalMode = (event) => {
    queryHydrationRef.current = null;
    const mode = event.target.value;
    setNominalMode(mode);
    setNominal('');
    setPitch('');
    setPitchMode(mode === 'custom' ? 'custom' : 'listed');
    clearCalculation();
  };

  const updateListedNominal = (event) => {
    queryHydrationRef.current = null;
    const value = event.target.value;
    const selected = summary?.nominalSizes.find(item => Number(item.nominalMm) === Number(value));
    setNominal(value);
    setPitch(selected?.defaultPitchMm != null ? formatMetricIdentityNumber(selected.defaultPitchMm) : '');
    setPitchMode('listed');
    clearCalculation();
  };

  const updateCustomNominal = (event) => {
    queryHydrationRef.current = null;
    setNominal(event.target.value);
    setPitch('');
    setPitchMode('custom');
    clearCalculation();
  };

  const updatePitchMode = (event) => {
    queryHydrationRef.current = null;
    const mode = event.target.value;
    setPitchMode(mode);
    setPitch(mode === 'listed' && nominalCatalog?.defaultPitchMm != null
      ? formatMetricIdentityNumber(nominalCatalog.defaultPitchMm) : '');
    clearCalculation();
  };

  const validate = () => {
    if (!positiveNumber(nominal)) return 'Enter or select a positive nominal diameter.';
    if (!positiveNumber(pitch)) return 'Enter or select a positive pitch.';
    if (!toleranceClass) return 'Select a tolerance class.';
    if (!summary?.toleranceClasses?.values.includes(toleranceClass)) return 'Select a supported tolerance class.';
    return '';
  };

  const submit = (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setResult(null);
      setError(validationError);
      return;
    }
    const canonical = {
      side,
      nominal: formatMetricIdentityNumber(nominal),
      pitch: formatMetricIdentityNumber(pitch),
      tolerance: toleranceClass,
    };
    const nextSignature = new URLSearchParams(canonical).toString();
    if (nextSignature === querySignature) {
      processedQueryRef.current = querySignature;
      runCalculation({ nominalMm: Number(nominal), pitchMm: Number(pitch), side, toleranceClass });
    } else {
      processedQueryRef.current = '';
      setSearchParams(canonical);
    }
  };

  const pitchGroups = PITCH_CATEGORIES.map(category => ({
    category,
    pitches: nominalCatalog?.pitches.filter(item => item.category === category) || [],
  })).filter(group => group.pitches.length > 0);

  return (
    <main className="metricThreadPage">
      <header className="metricThreadHeader">
        <p>Threads · Metric M</p>
        <h1>{METRIC_THREAD_H1}</h1>
        <span>Select a listed thread or enter a custom metric diameter and pitch to calculate supported internal or external thread limits.</span>
      </header>

      <form className="metricThreadForm" onSubmit={submit} noValidate>
        <fieldset className="metricSegmented">
          <legend>Thread side</legend>
          <div>
            {['internal', 'external'].map(value => (
              <label key={value} className={side === value ? 'active' : ''}>
                <input type="radio" name="thread-side" value={value} checked={side === value} onChange={updateSide} />
                <span>{value === 'internal' ? 'Internal' : 'External'}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {summaryCatalogError && <p className="metricError" role="alert">{summaryCatalogError}</p>}

        <fieldset className="metricChoiceFieldset">
          <legend>Nominal diameter</legend>
          <div className="metricChoiceTabs">
            <label><input type="radio" name="nominal-mode" value="listed" checked={nominalMode === 'listed'} onChange={updateNominalMode} /> Listed size</label>
            <label><input type="radio" name="nominal-mode" value="custom" checked={nominalMode === 'custom'} onChange={updateNominalMode} /> Custom diameter</label>
          </div>
          {nominalMode === 'listed' ? (
            <div className="metricField">
              <label htmlFor="metric-nominal">Metric nominal size</label>
              <select id="metric-nominal" value={nominal} onChange={updateListedNominal} disabled={catalogLoading || !summary}>
                <option value="">Select a size</option>
                {summary?.nominalSizes.map(item => <option key={item.nominalMm} value={item.nominalMm}>M{item.nominalMm}</option>)}
              </select>
            </div>
          ) : (
            <div className="metricField">
              <label htmlFor="metric-custom-nominal">Custom nominal diameter [mm]</label>
              <input id="metric-custom-nominal" type="number" min="0" step="any" inputMode="decimal" value={nominal} onChange={updateCustomNominal} placeholder="103" />
              <small>Metric M identity remains in millimetres.</small>
            </div>
          )}
        </fieldset>

        <fieldset className="metricChoiceFieldset">
          <legend>Pitch</legend>
          <div className="metricChoiceTabs">
            <label><input type="radio" name="pitch-mode" value="listed" checked={pitchMode === 'listed'} onChange={updatePitchMode} disabled={!nominalCatalog?.listed} /> Listed pitch</label>
            <label><input type="radio" name="pitch-mode" value="custom" checked={pitchMode === 'custom'} onChange={updatePitchMode} /> Custom pitch</label>
          </div>
          {nominalCatalogError && <p className="metricError" role="alert">{nominalCatalogError}</p>}
          {pitchMode === 'listed' ? (
            <div className="metricField">
              <label htmlFor="metric-pitch">Metric pitch</label>
              <select id="metric-pitch" value={pitch} onChange={(event) => { queryHydrationRef.current = null; setPitch(event.target.value); clearCalculation(); }} disabled={!nominalCatalog?.listed}>
                <option value="">Select a pitch</option>
                {pitchGroups.map(group => (
                  <optgroup key={group.category} label={metricPitchLabels[group.category]}>
                    {group.pitches.map(item => <option key={item.pitchMm} value={item.pitchMm}>{Number(item.pitchMm).toFixed(2)} mm — {metricPitchLabels[item.category]}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          ) : (
            <div className="metricField">
              <label htmlFor="metric-custom-pitch">Custom pitch [mm]</label>
              <input id="metric-custom-pitch" type="number" min="0" step="any" inputMode="decimal" value={pitch} onChange={(event) => { queryHydrationRef.current = null; setPitch(event.target.value); clearCalculation(); }} placeholder="6" />
              <small>A custom pitch is not classified as “Other” unless that exact combination is listed by the catalog.</small>
            </div>
          )}
        </fieldset>

        <div className="metricField">
          <label htmlFor="metric-tolerance">Tolerance class</label>
          <select id="metric-tolerance" value={toleranceClass} onChange={(event) => { setToleranceClass(event.target.value); clearCalculation(); }} disabled={catalogLoading || !summary}>
            <option value="">Select a class</option>
            {summary?.toleranceClasses?.values.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
          <small>Classes shown are supported by the backend for the selected thread side.</small>
        </div>

        {error && <p className="metricError" role="alert">{error}</p>}
        <button className="metricSubmit" type="submit" disabled={loading || catalogLoading || Boolean(summaryCatalogError)}>{loading ? 'Calculating…' : 'Calculate thread'}</button>
      </form>

      <div className="metricResultArea" aria-live="polite" aria-busy={loading}>
        {result && (
          <MetricThreadResult
            result={result}
            preDrillResult={preDrillResult}
            preDrillLoading={preDrillLoading}
            preDrillError={preDrillError}
            formingDiameterInput={formingDiameterInput}
            onFormingDiameterChange={setFormingDiameterInput}
          />
        )}
      </div>

      <MetricThreadSeoContent />
    </main>
  );
};

export default MetricThreadPage;
